import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save } from "lucide-react";
import SearchableSelect from "../../components/SearchableSelect";
import { api } from "../../lib/api";
import { RouteError } from "../shared/PageState";
import "./organizationPocs.css";

export type OrganizationProjectPocView = {
  default_poc: { user_id: string; name: string | null; mobile: string | null } | null;
  inactive_default_poc: boolean;
  updated_at: string;
  users: { user_id: string; name: string | null; mobile: string | null }[];
};

export default function OrganizationProjectPoc({ organizationId, canEdit }: { organizationId: string; canEdit: boolean }) {
  const client = useQueryClient();
  const queryKey = ["organization-project-poc", organizationId];
  const query = useQuery({ queryKey, queryFn: () => api.getOrganizationProjectPoc(organizationId) });
  const [draft, setDraft] = useState<{ userId: string; updatedAt: string } | null>(null);
  const save = useMutation({
    mutationFn: (value: { userId: string; updatedAt: string }) => api.updateOrganizationProjectPoc(organizationId, value.userId, value.updatedAt),
    onSuccess: data => {
      client.setQueryData(queryKey, data);
      setDraft(null);
      void client.invalidateQueries({ queryKey: ["tech-ops-organization", organizationId] });
      void client.invalidateQueries({ queryKey: ["tech-admin-audit"] });
    }
  });
  if (query.isPending) return <div className="pa-panel pa-detail-padding" role="status">Loading project contact…</div>;
  if (query.isError) return <RouteError title="Project contact unavailable" detail={query.error.message} onRetry={() => query.refetch()} />;
  const data = query.data;
  const value = draft?.userId ?? data.default_poc?.user_id ?? "";
  const changed = !!draft && value !== (data.default_poc?.user_id ?? "");
  const unavailable = !!value && !data.users.some(user => user.user_id === value);
  return <form className="pa-panel pa-detail-padding pa-poc-panel" onSubmit={event => { event.preventDefault(); if (canEdit && draft && changed && !unavailable) save.mutate(draft); }}>
    <div className="pa-title-row"><div><h2>Project contact</h2><p>This contact is suggested for new projects and approval requests. A different contact can be confirmed for each project.</p></div></div>
    <div className="pa-org-poc-field">
      <span className="pa-org-poc-label">Default project POC</span>
      {canEdit ? <SearchableSelect label="Default project POC" value={value}
        options={data.users.map(user => ({ value: user.user_id, label: user.name || user.mobile || "Staff member", description: user.name ? user.mobile ?? undefined : undefined }))}
        onChange={userId => { setDraft(previous => ({ userId, updatedAt: previous?.updatedAt ?? data.updated_at })); save.reset(); }}
        disabled={save.isPending || !data.users.length} placeholder={data.inactive_default_poc ? "Choose a replacement contact" : "Select a staff member"}
        searchPlaceholder="Search name or mobile" emptyMessage="No matching staff." />
        : <p>{data.default_poc ? [data.default_poc.name, data.default_poc.mobile].filter(Boolean).join(" · ") : "No active contact assigned"}</p>}
    </div>
    {data.inactive_default_poc && <p className="pa-form-error">The saved contact is no longer active in this organization. Choose another staff member.</p>}
    {!data.users.length && <p className="pa-form-note">No active staff are available. Add or activate a staff member before assigning the project contact.</p>}
    {unavailable && <p className="pa-form-error" role="alert">The selected staff member is no longer available. Choose another contact.</p>}
    <p className="pa-form-note">Existing projects keep their confirmed contact. Billing and shipping addresses do not affect this selection.</p>
    {save.isError && <div><p className="pa-form-error" role="alert">{save.error.message}</p><button type="button" className="pa-secondary-button" onClick={() => { setDraft(null); save.reset(); void query.refetch(); }}>Refresh contact</button></div>}
    {save.isSuccess && <p className="pa-form-success" role="status">Default project POC saved.</p>}
    {canEdit ? <div className="pa-detail-actions"><button type="submit" className="pa-primary-button" disabled={!changed || unavailable || save.isPending}><Save size={16} />{save.isPending ? "Saving…" : "Save changes"}</button></div>
      : <p className="pa-form-note">Tech Admin access is required to change this contact.</p>}
  </form>;
}
